import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Button } from '../components';
import { Users, User, Calendar, Briefcase, Star, X, UserPlus } from 'lucide-react';
import api from '../services/api';
import { useEmployerOpdrachten, OpdrachtStatus } from '../hooks/useOpdrachten';
import { ReplacementModal } from '../components/ReplacementModal';
import { useCockpitTitle } from '../components/EmployerCockpitLayout';
import StatusDot from '../components/StatusDot';

import {
  PROFESSIONAL_TYPE_LABELS,
  PROFESSIONAL_CATEGORIES,
  type ProfessionalCategoryId,
} from '../types/professionalRates';

export const EmployerConnectionsPage: React.FC = () => {
  const { t } = useTranslation('employer');
  useCockpitTitle(t('connections.pageTitle', 'Samenwerkingen'), t('connections.subtitle', 'Ontdek nieuwe kansen en beheer je projecten'));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'connections' | 'opdrachten'>('connections');
  const [opdrachtStatus, setOpdrachtStatus] = useState<OpdrachtStatus | undefined>(undefined);

  // Sync from URL params (e.g. from cockpit "Reactie professioneels" link)
  useEffect(() => {
    const tab = searchParams.get('tab');
    const status = searchParams.get('status') as OpdrachtStatus | null;
    if (tab === 'opdrachten') {
      setActiveTab('opdrachten');
      if (status && ['nieuw', 'geaccepteerd', 'onderhandelingen', 'afgerond', 'afgewezen'].includes(status)) {
        setOpdrachtStatus(status);
      } else {
        setOpdrachtStatus(undefined);
      }
    }
  }, [searchParams]);
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [reviewModalConnection, setReviewModalConnection] = useState<{
    professional_id: string;
    professional_name: string;
    average_rating: number;
    review_count: number;
    star_distribution?: Record<number, number>;
    questions: Array<{ label: string; question: string; avg: number }>;
  } | null>(null);
  const [replacementModal, setReplacementModal] = useState<{
    connectionId: number;
    professionalName: string;
  } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['employer-connections'],
    queryFn: async () => {
      const response = await api.get('/employer/connections');
      return response.data.data || [];
    },
  });

  const { data: reviewsData } = useQuery({
    queryKey: ['employer-reviews-connections'],
    queryFn: async () => {
      const response = await api.get('/employer/reviews');
      return response.data.data || [];
    },
  });

  const reviewsByProfessional = useMemo(() => {
    const map: Record<string, { average_rating: number; review_count: number; reviews_visible: boolean; reviews_needed: number; star_distribution?: Record<number, number>; questions?: Array<{ label: string; question: string; avg: number }> }> = {};
    for (const r of reviewsData || []) {
      map[r.professional_id] = {
        average_rating: r.average_rating ?? 0,
        review_count: r.review_count ?? 0,
        // AVG-drempel van 3: scores blijven verborgen tot er drie evaluaties zijn
        reviews_visible: r.reviews_visible !== false,
        reviews_needed: r.reviews_needed ?? 0,
        star_distribution: r.star_distribution,
        questions: r.questions
      };
    }
    return map;
  }, [reviewsData]);

  const filteredConnections = useMemo(() => {
    const connections = data || [];
    if (!roleFilter) return connections;
    return connections.filter((c: any) => {
      const types = c.professional_types || [];
      const role = c.connection_role || types[0];
      return types.includes(roleFilter) || role === roleFilter;
    });
  }, [data, roleFilter]);

  const { data: opdrachten, isLoading: isLoadingOpdrachten } = useEmployerOpdrachten(opdrachtStatus);

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <p className="text-gray-600">{t('common:actions.loading', 'Laden...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <p className="text-red-600">{t('connections.loadError', 'Er is een fout opgetreden bij het laden van de samenwerkingen.')}</p>
        </div>
      </div>
    );
  }

  const connections = filteredConnections;

  const opdrachtTabs: { value: OpdrachtStatus | undefined; label: string }[] = [
    { value: undefined, label: t('connections.filterAll', 'Alle') },
    { value: 'nieuw', label: t('applicationStatus.nieuw', 'Nieuw') },
    { value: 'geaccepteerd', label: t('applicationStatus.geaccepteerd', 'Geaccepteerd') },
    { value: 'onderhandelingen', label: t('applicationStatus.onderhandelingen', 'Onderhandelingen') },
    { value: 'afgerond', label: t('applicationStatus.afgerond', 'Afgerond') },
    { value: 'afgewezen', label: t('applicationStatus.afgewezen', 'Afgewezen') },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      {/* Main tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
          <button
            onClick={() => setActiveTab('connections')}
            className={`
              py-4 px-1 border-b-2 font-medium text-sm
              ${
                activeTab === 'connections'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <Users className="w-4 h-4 inline mr-2" />
            {t('connections.tabConnections', 'Samenwerkingen')}
          </button>
          <button
            onClick={() => setActiveTab('opdrachten')}
            className={`
              py-4 px-1 border-b-2 font-medium text-sm
              ${
                activeTab === 'opdrachten'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <Briefcase className="w-4 h-4 inline mr-2" />
            {t('connections.tabAssignments', 'Opdrachten')}
          </button>
        </nav>
      </div>

      {/* Opdrachten status tabs */}
      {activeTab === 'opdrachten' && (
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
            {opdrachtTabs.map((tab) => (
              <button
                key={tab.value || 'all'}
                onClick={() => setOpdrachtStatus(tab.value)}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm
                  ${
                    opdrachtStatus === tab.value
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Connections content */}
      {activeTab === 'connections' && (
        <>
        {connections.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Users className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('connections.noConnections', 'Geen samenwerkingen')}
            </h2>
            <p className="text-gray-600 mb-4">
              {t('connections.noConnectionsDesc', 'U heeft nog geen actieve samenwerkingen met professionals.')}
            </p>
            <Link to="/employer/dashboard/zoeken/uitgebreid">
              <Button>{t('connections.findProfessionals', 'Professionals')}</Button>
            </Link>
          </div>
        </Card>
      ) : (
        <>
        <div className="mb-4 flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">{t('cockpit.filterByRole', 'Filter op rol:')}</label>
          <select
            title={t('connections.filterRoleTitle', 'Toon alleen professionals met deze rol (bijv. Bedrijfsmaatschappelijk Werker)')}
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">Alle rollen</option>
            {(Object.entries(PROFESSIONAL_CATEGORIES) as [ProfessionalCategoryId, typeof PROFESSIONAL_CATEGORIES[ProfessionalCategoryId]][]).map(([catId, cat]) => (
              <optgroup key={catId} label={cat.label}>
                {cat.types.map(type => (
                  <option key={type} value={type}>{PROFESSIONAL_TYPE_LABELS[type]}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {connections.map((connection: any) => {
            const rev = reviewsByProfessional[connection.professional_id] ?? {
              average_rating: connection.average_rating ?? null,
              review_count: connection.review_count ?? 0,
              questions: undefined
            };
            return (
            <Card key={connection.id} className="hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-gray-900">
                        {connection.first_name} {connection.last_name}
                      </h3>
                      {/* Interne poolstatus (dubbele status-grendel): realtime uit agenda + verlof */}
                      {connection.pool_status && (
                        <StatusDot
                          status={connection.pool_status}
                          beschikbaarVanaf={connection.pool_beschikbaar_vanaf}
                        />
                      )}
                      {rev.review_count > 0 && !rev.reviews_visible && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium"
                          title={t('connections.reviewsThresholdHint', 'Reviews zijn pas zichtbaar vanaf 3 ingestuurde evaluaties (AVG)')}
                        >
                          <Star className="w-3.5 h-3.5 text-gray-400" />
                          {t('connections.reviewsThreshold', 'Nog {{count}} evaluatie(s) nodig', { count: rev.reviews_needed })}
                        </span>
                      )}
                      {rev.review_count > 0 && rev.reviews_visible && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setReviewModalConnection({
                              professional_id: connection.professional_id,
                              professional_name: `${connection.first_name || ''} ${connection.last_name || ''}`.trim(),
                              average_rating: rev.average_rating ?? 0,
                              review_count: rev.review_count,
                              star_distribution: rev.star_distribution,
                              questions: (rev.questions || []).map((q: any) => ({
                                label: q.label,
                                question: q.question,
                                avg: q.avg ?? 0
                              }))
                            });
                          }}
                          className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 hover:bg-amber-100 text-xs font-medium"
                        >
                          <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
                          {rev.average_rating?.toFixed(1) ?? '-'} ({rev.review_count})
                        </button>
                      )}
                    </div>
                    {(connection.professional_type_labels && connection.professional_type_labels.length > 0) ? (
                      <p className="text-sm text-blue-600 font-medium">
                        {t('connections.role', 'Rol')}: {connection.professional_type_labels.join(', ')}
                      </p>
                    ) : connection.connection_role ? (
                      <p className="text-sm text-blue-600 font-medium">
                        {t('connections.role', 'Rol')}: {(PROFESSIONAL_TYPE_LABELS as Record<string, string>)[connection.connection_role] || connection.connection_role}
                      </p>
                    ) : null}
                    <p className="text-sm text-gray-500">{connection.email}</p>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span>
                      {connection.connected_at
                        ? new Date(connection.connected_at).toLocaleDateString('nl-NL')
                        : t('connections.notConnected', 'Niet verbonden')}
                    </span>
                  </div>
                  <div className="flex items-center flex-wrap gap-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      connection.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : connection.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {connection.status === 'active' ? t('connections.statusActive', 'Actief')
                        : connection.status === 'pending' ? t('connections.statusPending', 'In afwachting')
                        : connection.status}
                    </span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    className="flex-1 min-w-0"
                    onClick={() => navigate(`/employer/professional/${connection.professional_id}`)}
                    type="button"
                  >
                    {t('connections.viewProfile', 'Bekijk Profiel')}
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 min-w-0"
                    onClick={() => navigate(`/employer/connections/${connection.id}/agreements`)}
                    type="button"
                  >
                    {t('connections.agreements', 'Afspraken')}
                  </Button>
                  <Button
                    className="flex-1 min-w-0"
                    onClick={() => navigate(`/employer/dossiers?professional_id=${connection.professional_id}`)}
                    type="button"
                  >
                    {t('connections.dossiers', 'Dossiers')}
                  </Button>
                </div>
                {connection.status === 'active' && (
                  <button
                    type="button"
                    onClick={() => setReplacementModal({
                      connectionId: connection.id,
                      professionalName: `${connection.first_name || ''} ${connection.last_name || ''}`.trim()
                    })}
                    className="mt-2 w-full flex items-center justify-center gap-1.5 py-1.5 px-3 border border-gray-200 rounded-md text-xs text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                  >
                    <UserPlus className="w-3.5 h-3.5" />
                    {t('connections.viewReplacements', 'Vervangers bekijken')}
                  </button>
                )}
              </div>
            </Card>
          );
          })}
        </div>
        </>
      )}

      {replacementModal && (
        <ReplacementModal
          connectionId={replacementModal.connectionId}
          professionalName={replacementModal.professionalName}
          mode="employer"
          onClose={() => setReplacementModal(null)}
        />
      )}

      {reviewModalConnection && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('cockpit.ratingsTitle', 'Beoordelingen – {{name}}', { name: reviewModalConnection.professional_name })}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t('connections.reviewsAnonymous', 'Anonieme beoordelingen van medewerkers. U kunt niet zien welke medewerker welke beoordeling heeft gegeven.')}
                  </p>
                </div>
                <button
                  onClick={() => setReviewModalConnection(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex items-center gap-2 mb-6">
                <div className="flex items-center">
                  <Star className="w-6 h-6 fill-amber-500 text-amber-500" />
                  <span className="ml-1 text-xl font-semibold text-gray-900">
                    {reviewModalConnection.average_rating.toFixed(1)}
                  </span>
                </div>
                <span className="text-sm text-gray-500">
                  {t('connections.averageOfReviews', 'Gemiddelde van {{count}} review{{suffix}}', {
                    count: reviewModalConnection.review_count,
                    suffix: reviewModalConnection.review_count !== 1 ? 's' : ''
                  })}
                </span>
              </div>
              {reviewModalConnection.star_distribution && reviewModalConnection.review_count > 0 && (
                <div className="space-y-2 mb-6">
                  <h4 className="text-sm font-medium text-gray-700">{t('connections.starDistribution', 'Verdeling sterren')}</h4>
                  {[5, 4, 3, 2, 1].map((stars) => {
                    const count = reviewModalConnection.star_distribution![stars] ?? 0;
                    const pct = (count / reviewModalConnection.review_count) * 100;
                    return (
                      <div key={stars} className="flex items-center gap-3">
                        <div className="flex gap-0.5 w-24 flex-shrink-0">
                          {[1, 2, 3, 4, 5].map((v) => (
                            <Star
                              key={v}
                              className={`w-4 h-4 ${v <= stars ? 'fill-amber-400 text-amber-400' : 'text-gray-200'}`}
                            />
                          ))}
                        </div>
                        <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
                          <div
                            className="h-full bg-amber-400 rounded transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-600 w-12 text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              {reviewModalConnection.questions.length > 0 && (
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-700">{t('connections.averagePerQuestion', 'Gemiddelde per vraag')}</h4>
                  {reviewModalConnection.questions.map((q: any, i: number) => (
                    <div key={i} className="border-b border-gray-100 pb-3 last:border-0">
                      <p className="text-sm font-medium text-gray-800">{q.label}</p>
                      <p className="text-xs text-gray-600 mb-1">{q.question}</p>
                      <div className="flex items-center gap-2">
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((v) => (
                            <Star
                              key={v}
                              className={`w-4 h-4 ${
                                q.avg >= v ? 'fill-amber-400 text-amber-400' : 'text-gray-200'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-sm text-gray-600">
                          {q.avg > 0 ? q.avg.toFixed(1) : '-'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {/* Opdrachten content */}
      {activeTab === 'opdrachten' && (
        <>
          {isLoadingOpdrachten ? (
            <div className="text-center py-12">
              <p className="text-gray-600">{t('connections.loadingAssignments', 'Opdrachten laden...')}</p>
            </div>
          ) : opdrachten && opdrachten.length > 0 ? (
            <div className="space-y-6">
              {opdrachten.map((opdracht: any) => (
                <Card key={opdracht.id || opdracht.vacancy_id} className="hover:shadow-md transition-shadow">
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {opdracht.titel}
                        </h3>
                        <p className="text-gray-600 mb-4 line-clamp-2">
                          {opdracht.beschrijving}
                        </p>
                        {opdracht.professional_first_name && (
                          <div className="flex items-center text-sm text-gray-600 mb-2">
                            <User className="w-4 h-4 mr-2" />
                            <span>
                              {opdracht.professional_first_name} {opdracht.professional_last_name}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            opdracht.application_status === 'nieuw' ? 'bg-blue-100 text-blue-800' :
                            opdracht.application_status === 'geaccepteerd' ? 'bg-green-100 text-green-800' :
                            opdracht.application_status === 'onderhandelingen' ? 'bg-yellow-100 text-yellow-800' :
                            opdracht.application_status === 'afgerond' ? 'bg-gray-100 text-gray-800' :
                            opdracht.application_status === 'afgewezen' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {opdracht.application_status}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Link to={`/opdrachten/${opdracht.vacancy_id || opdracht.id}`}>
                        <Button variant="outline" size="sm">
                          {t('connections.details', 'Details')}
                        </Button>
                      </Link>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <div className="text-center py-12">
                <Briefcase className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  {t('connections.noAssignments', 'Geen opdrachten')}
                </h2>
                <p className="text-gray-600">
                  {t('connections.noAssignmentsForStatus', 'Er zijn geen opdrachten gevonden voor deze status.')}
                </p>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
};
